import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { OrderMinimumsReport } from '../services/api';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, typography } from '../theme';

const money = (currency: string, amount: number) => `${currency} ${Number(amount || 0).toFixed(2)}`;

/**
 * Shows unmet order minimums — product minimum quantities and vendor minimum
 * order amounts (with progress) — from a useOrderMinimums report. Renders
 * nothing when every minimum is met.
 */
export const OrderMinimumsNotice: React.FC<{ report: OrderMinimumsReport | null; style?: any }> = ({ report, style }) => {
  const s = useThemedRTLStyles(getStyles);
  const { t } = useLocale();
  if (!report) return null;
  const products = report.products.filter((p) => !p.met);
  const vendors = report.vendors.filter((v) => !v.met);
  if (products.length === 0 && vendors.length === 0) return null;

  return (
    <View style={[s.box, style]}>
      <View style={s.titleRow}>
        <Ionicons name="alert-circle" size={18} color={colors.warning as string} />
        <Text style={s.title}>{t('orderMinimums.title')}</Text>
      </View>

      {products.map((p) => (
        <Text key={p.productId} style={s.line}>
          {t('orderMinimums.productLine', { name: p.name, min: p.minimumOrderQuantity, qty: p.quantity })}
        </Text>
      ))}

      {vendors.map((v) => {
        const pct = Math.max(0, Math.min(1, v.minimumOrderAmount > 0 ? v.amount / v.minimumOrderAmount : 1));
        return (
          <View key={v.vendorId} style={s.vendor}>
            <Text style={s.line}>
              {t('orderMinimums.vendorLine', {
                name: v.name,
                min: money(v.currency, v.minimumOrderAmount),
                remaining: money(v.currency, Math.max(0, v.minimumOrderAmount - v.amount)),
              })}
            </Text>
            <View style={s.track}>
              <View style={[s.fill, { width: `${Math.round(pct * 100)}%` }]} />
            </View>
            <Text style={s.progressText}>
              {money(v.currency, v.amount)} / {money(v.currency, v.minimumOrderAmount)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    box: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.warning,
      backgroundColor: colors.background.surface,
      padding: 12,
      gap: 6,
    },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    title: { flex: 1, fontSize: 14, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    line: { fontSize: 13, lineHeight: 19, fontFamily: typography.fontFamily.primary, color: colors.text.primary },
    vendor: { gap: 4, marginTop: 2 },
    track: { height: 6, borderRadius: 3, overflow: 'hidden', backgroundColor: colors.background.secondary },
    fill: { height: 6, borderRadius: 3, backgroundColor: colors.warning },
    progressText: { fontSize: 11, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  });
